import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import type { AutocompleteOption } from "@/components/Autocomplete";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { getUserLocalLogin, useCurrentUser } from "@/lib/auth";
import { toast } from "@/lib/toast";
import {
  employeeTimeEntryToProjectOption,
  isEmployeeTimeEntryLocked,
} from "@/lib/auftragAutocomplete";
import type { AuftragLookup } from "@/lib/material";
import {
  computeHoursFromTimeRange,
  formatHours,
  getEmployeeDayCompliance,
  listEmployeeTimes,
  saveDayJustification,
  saveEmployeeDayBatch,
  type EmployeeTimeEntry,
} from "@/lib/employeeTimes";
import {
  getCachedEmployeeTimeProfile,
  invalidateCaptureCalendarCache,
} from "@/pages/admin/zeiterfassung/captureCalendarCache";
import {
  JUSTIFICATION_HINT,
  formatHoursHm,
  formatJustificationCounter,
  isJustificationValid,
  justificationRegimeApplies,
  normalizeJustification,
  validateJustificationText,
  type EmployeeTimeDayCompliance,
} from "@/lib/employeeTimeCompliance";
import type { WorkTimeModelDay } from "@/lib/workTimeModels";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  EmployeeLockedAutocomplete,
  employeeLoginToOption,
  type EmployeeOption,
} from "@/pages/admin/zeiterfassung/EmployeeLockedAutocomplete";
import {
  breakDurationMinutes,
  compareTimeHHMM,
  findDayEntryOverlapError,
  formatDurationInputFromRange,
  formatDurationInputHours,
  isUnassignedGapFillRow,
  parseDurationInput,
  resolveDayEntryOverlaps,
  resolveEmployeeWorkDay,
  rowHasContent,
  rowHasTimes,
  suggestBreakRange,
  suggestNextWorkTimeFrom,
  syncDayEntryTimeFields,
  type DayEntryRowKind,
} from "@/pages/admin/zeiterfassung/dayEntryTimeUtils";
import { CaptureMonthCalendar } from "@/pages/admin/zeiterfassung/CaptureMonthCalendar";
import { DayEntryMobileCards } from "@/pages/admin/zeiterfassung/DayEntryMobileCards";
import { MIXED_PROJECT_POSITION_REQUIRED } from "@/pages/admin/zeiterfassung/preisartChoice";

interface SelectedEmployee {
  login: string;
  display: string;
}

export interface DayEntryRow {
  clientKey: string;
  id?: number;
  readOnly?: boolean;
  /** Projekt fertiggestellt / abgerechnet / bezahlt – Zeiten gesperrt. */
  projectLocked?: boolean;
  rowKind: DayEntryRowKind;
  project: AutocompleteOption<AuftragLookup> | null;
  /** App-Angebotsposition. Bei Nachweis- und Festpreis-Projekten Pflicht. */
  positionId: number | null;
  /** Projekt hat Nachweis- und Festpreispositionen. */
  positionRequired?: boolean;
  timeFrom: string;
  timeTo: string;
  /** Eingabe in Stunden (z. B. 1,5) oder H:MM (z. B. 1:30) – steuert Bis automatisch. */
  durationInput: string;
  comment: string;
  /** Homeoffice (nur Arbeitszeiten; für Steuer / Homeoffice-Pauschale). */
  isHomeoffice: boolean;
  markedDelete?: boolean;
}

interface DayEntryTableProps {
  defaultEmployee: SelectedEmployee;
  onSaved: () => void;
  allowEmployeePicker?: boolean;
}

let rowKeyCounter = 0;
function nextRowKey(): string {
  rowKeyCounter += 1;
  return `row-${rowKeyCounter}`;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function yesterdayIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function shiftDateIso(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDayLabelDe(iso: string): string {
  if (iso === todayIso()) return "Heute";
  if (iso === yesterdayIso()) return "Gestern";
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString("de-DE", { weekday: "long" });
}

function buildNewWorkRow(
  contentRows: DayEntryRow[],
  workDay: WorkTimeModelDay | null,
): DayEntryRow {
  return {
    clientKey: nextRowKey(),
    rowKind: "work",
    project: null,
    positionId: null,
    timeFrom: suggestNextWorkTimeFrom(contentRows.filter(rowHasContent), workDay),
    timeTo: "",
    durationInput: "",
    comment: "",
    isHomeoffice: false,
  };
}

function buildGapFillWorkRow(timeFrom: string, timeTo: string): DayEntryRow {
  return {
    clientKey: nextRowKey(),
    rowKind: "work",
    project: null,
    positionId: null,
    timeFrom: timeFrom.slice(0, 5),
    timeTo: timeTo.slice(0, 5),
    durationInput: formatDurationInputFromRange(timeFrom, timeTo),
    comment: "",
    isHomeoffice: false,
  };
}

function insertVisibleRowAfter(
  rows: DayEntryRow[],
  afterClientKey: string,
  newRow: DayEntryRow,
): DayEntryRow[] {
  const visible = rows.filter((r) => !r.markedDelete);
  const afterIdx = visible.findIndex((r) => r.clientKey === afterClientKey);
  if (afterIdx < 0) return [...rows, newRow];

  const insertBeforeKey =
    afterIdx < visible.length - 1 ? visible[afterIdx + 1]!.clientKey : null;
  if (!insertBeforeKey) {
    const fullIdx = rows.findIndex((r) => r.clientKey === afterClientKey);
    return [...rows.slice(0, fullIdx + 1), newRow, ...rows.slice(fullIdx + 1)];
  }

  const fullIdx = rows.findIndex((r) => r.clientKey === insertBeforeKey);
  return [...rows.slice(0, fullIdx), newRow, ...rows.slice(fullIdx)];
}

/** Leere Folgezeile, die noch am alten Bis hängt, auf das neue Bis nachziehen. */
function followPreviousEndIfUnset(
  rows: DayEntryRow[],
  clientKey: string,
  oldTimeTo: string,
  newTimeTo: string,
): DayEntryRow[] {
  const oldEnd = oldTimeTo.slice(0, 5);
  const newEnd = newTimeTo.slice(0, 5);
  if (!oldEnd || !newEnd || oldEnd === newEnd) return rows;

  const visible = rows.filter((r) => !r.markedDelete);
  const rowIdx = visible.findIndex((r) => r.clientKey === clientKey);
  const following = rowIdx >= 0 ? visible[rowIdx + 1] : undefined;
  if (!following || following.readOnly || following.rowKind !== "work") return rows;
  if (following.id || following.timeTo || following.project) return rows;
  if (following.timeFrom.slice(0, 5) !== oldEnd) return rows;

  return rows.map((r) =>
    r.clientKey === following.clientKey ? { ...r, timeFrom: newEnd } : r,
  );
}

function closeGapAfterRowDelete(rows: DayEntryRow[], deletedRow: DayEntryRow): DayEntryRow[] {
  if (!rowHasTimes(deletedRow)) return rows;

  const visible = rows.filter((r) => !r.markedDelete);
  const deletedIdx = visible.findIndex((r) => r.clientKey === deletedRow.clientKey);
  const nextRow = deletedIdx >= 0 ? visible[deletedIdx + 1] : undefined;
  if (!nextRow) return rows;

  const newFrom = deletedRow.timeFrom.slice(0, 5);
  if (nextRow.timeFrom.slice(0, 5) === newFrom) return rows;

  return rows.map((r) =>
    r.clientKey === nextRow.clientKey
      ? { ...r, ...syncDayEntryTimeFields(r, { timeFrom: newFrom }) }
      : r,
  );
}

function entryToRow(entry: EmployeeTimeEntry): DayEntryRow {
  const isBreak = entry.entry_type === "break";
  const isHero = entry.entry_type === "hero" || (entry.id != null && entry.id < 0);
  const projectLocked = !isBreak && isEmployeeTimeEntryLocked(entry);
  const timeFrom = entry.time_from?.slice(0, 5) || "";
  const timeTo = entry.time_to?.slice(0, 5) || "";
  const durationFromHours =
    isHero && Number.isFinite(entry.hours_decimal) && entry.hours_decimal > 0
      ? formatDurationInputHours(entry.hours_decimal)
      : "";
  return {
    clientKey: nextRowKey(),
    id: entry.id,
    readOnly: isHero || projectLocked,
    projectLocked,
    rowKind: isBreak ? "break" : "work",
    project: isBreak ? null : employeeTimeEntryToProjectOption(entry),
    positionId: isBreak ? null : entry.geschaeftsdokument_position_id ?? null,
    timeFrom,
    timeTo,
    durationInput:
      durationFromHours || formatDurationInputFromRange(timeFrom, timeTo),
    comment: entry.comment || (isBreak ? "Pause" : ""),
    isHomeoffice: !isBreak && Boolean(entry.is_homeoffice),
  };
}

export function DayEntryTable({
  defaultEmployee,
  onSaved,
  allowEmployeePicker = true,
}: DayEntryTableProps) {
  const { user } = useCurrentUser();
  const currentLogin = getUserLocalLogin(user);
  const [employeeOption, setEmployeeOption] = useState<AutocompleteOption<EmployeeOption> | null>(
    () => employeeLoginToOption(defaultEmployee.login, defaultEmployee.display),
  );

  useEffect(() => {
    if (defaultEmployee.login.trim()) {
      setEmployeeOption(employeeLoginToOption(defaultEmployee.login, defaultEmployee.display));
    }
  }, [defaultEmployee.login, defaultEmployee.display]);

  const employee = employeeOption?.value ?? { login: "", display: "" };
  const [workDate, setWorkDate] = useState(todayIso());
  const [rows, setRows] = useState<DayEntryRow[]>([buildNewWorkRow([], null)]);
  const [workDayModel, setWorkDayModel] = useState<WorkTimeModelDay | null>(null);
  const [projectRequired, setProjectRequired] = useState(false);
  const [homeofficeAllowed, setHomeofficeAllowed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [compliance, setCompliance] = useState<EmployeeTimeDayCompliance | null>(null);
  const [justificationDraft, setJustificationDraft] = useState("");
  const [savingJustification, setSavingJustification] = useState(false);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const isMobile = useMediaQuery("(max-width: 767px)");

  const loadDay = useCallback(async () => {
    const login = employee.login.trim();
    if (!login) {
      setLoading(false);
      setRows([buildNewWorkRow([], null)]);
      setWorkDayModel(null);
      setProjectRequired(false);
      setHomeofficeAllowed(false);
      setCompliance(null);
      return;
    }
    setLoading(true);
    try {
      const [entries, profile, workDay, dayCompliance] = await Promise.all([
        listEmployeeTimes({
          user_login: login,
          work_date_from: workDate,
          work_date_to: workDate,
        }),
        getCachedEmployeeTimeProfile(login),
        resolveEmployeeWorkDay(login, workDate),
        getEmployeeDayCompliance({ user_login: login, work_date: workDate }).catch(() => null),
      ]);
      setProjectRequired(profile.project_required);
      setHomeofficeAllowed(
        profile.homeoffice_allowed ?? (!profile.is_monteur && !profile.project_required),
      );
      setWorkDayModel(workDay);
      setCompliance(dayCompliance);
      setJustificationDraft(dayCompliance?.employee_justification ?? "");
      // API liefert neueste zuerst – für Overlap-Logik chronologisch sortieren,
      // sonst werden frühere Hero-Blöcke fälschlich nach hinten verschoben.
      const mapped = [...entries]
        .sort((a, b) => {
          const ta = a.time_from?.slice(0, 5) || "";
          const tb = b.time_from?.slice(0, 5) || "";
          if (ta !== tb) return compareTimeHHMM(ta || "99:99", tb || "99:99");
          return (a.id ?? 0) - (b.id ?? 0);
        })
        .map(entryToRow);
      const normalized = resolveDayEntryOverlaps(mapped);
      const trailing = buildNewWorkRow(normalized, workDay);
      setRows(normalized.length > 0 ? [...normalized, trailing] : [trailing]);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Tageszeiten konnten nicht geladen werden.");
      setRows([buildNewWorkRow([], null)]);
      setWorkDayModel(null);
    } finally {
      setLoading(false);
    }
  }, [employee.login, workDate]);

  useEffect(() => {
    void loadDay();
  }, [loadDay]);

  const activeRows = useMemo(
    () => rows.filter((r) => !r.markedDelete),
    [rows],
  );

  const dayTotal = useMemo(
    () =>
      activeRows.reduce((sum, row) => {
        if (row.rowKind === "break" || !row.timeFrom || !row.timeTo) return sum;
        return sum + computeHoursFromTimeRange(row.timeFrom, row.timeTo);
      }, 0),
    [activeRows],
  );

  const pauseMinutes = breakDurationMinutes(workDayModel);

  const ensureTrailingEmptyRow = useCallback(
    (nextRows: DayEntryRow[]) => {
      const visible = nextRows.filter((r) => !r.markedDelete);
      const content = visible.filter(rowHasContent);
      const last = visible[visible.length - 1];
      if (!last || rowHasContent(last)) {
        return [...nextRows, buildNewWorkRow(content, workDayModel)];
      }
      return nextRows;
    },
    [workDayModel],
  );

  const updateRow = (clientKey: string, patch: Partial<DayEntryRow>) => {
    setRows((prev) => {
      const currentRow = prev.find((r) => r.clientKey === clientKey);
      if (currentRow?.readOnly) return prev;

      // Dauer erst beim Blur anwenden — sonst wird „0:45“ schon bei „0:4“ zu 0,07
      // und die folgende „5“ ergibt fälschlich „0,075“ ≈ 0,08.
      const durationOnly =
        patch.durationInput !== undefined &&
        patch.timeFrom === undefined &&
        patch.timeTo === undefined;
      if (durationOnly) {
        return prev.map((row) =>
          row.clientKey === clientKey
            ? { ...row, ...patch, durationInput: patch.durationInput ?? "" }
            : row,
        );
      }

      const oldTimeTo = currentRow?.timeTo.slice(0, 5) ?? "";
      const next = prev.map((row) => {
        if (row.clientKey !== clientKey) return row;
        const timeFieldPatch: Partial<Pick<DayEntryRow, "timeFrom" | "timeTo" | "durationInput">> =
          {};
        if (patch.timeFrom !== undefined) timeFieldPatch.timeFrom = patch.timeFrom;
        if (patch.timeTo !== undefined) timeFieldPatch.timeTo = patch.timeTo;
        if (patch.durationInput !== undefined) timeFieldPatch.durationInput = patch.durationInput;
        const timePatch =
          Object.keys(timeFieldPatch).length > 0
            ? syncDayEntryTimeFields(row, timeFieldPatch)
            : null;
        return {
          ...row,
          ...patch,
          ...(timePatch ?? {}),
        };
      });

      const updatedRow = next.find((r) => r.clientKey === clientKey);
      const newTimeTo = updatedRow?.timeTo.slice(0, 5) ?? "";
      const followed = followPreviousEndIfUnset(next, clientKey, oldTimeTo, newTimeTo);
      return resolveDayEntryOverlaps(followed);
    });
  };

  const handleDurationBlur = (clientKey: string) => {
    setRows((prev) => {
      const row = prev.find((r) => r.clientKey === clientKey);
      if (!row) return prev;

      const oldTimeTo = row.timeTo;
      const next = prev.map((r) => {
        if (r.clientKey !== clientKey) return r;
        const hours = parseDurationInput(r.durationInput);
        if (!hours) {
          return {
            ...r,
            durationInput: formatDurationInputFromRange(r.timeFrom, r.timeTo),
          };
        }
        return { ...r, ...syncDayEntryTimeFields(r, { durationInput: r.durationInput }) };
      });

      const updated = next.find((r) => r.clientKey === clientKey);
      const followed = followPreviousEndIfUnset(
        next,
        clientKey,
        oldTimeTo,
        updated?.timeTo ?? "",
      );
      return resolveDayEntryOverlaps(followed);
    });
  };

  const handleRowBlur = (clientKey: string) => {
    setRows((prev) => {
      const row = prev.find((r) => r.clientKey === clientKey);
      if (!row || row.markedDelete) return prev;
      if (!rowHasContent(row)) return prev;
      return ensureTrailingEmptyRow(prev);
    });
  };

  const removeRow = (clientKey: string) => {
    const row = rows.find((r) => r.clientKey === clientKey);
    if (row?.readOnly) return;
    setRows((prev) => {
      const row = prev.find((r) => r.clientKey === clientKey);
      if (!row) return prev;

      const closed = closeGapAfterRowDelete(prev, row);

      if (row.id) {
        return ensureTrailingEmptyRow(
          closed.map((r) => (r.clientKey === clientKey ? { ...r, markedDelete: true } : r)),
        );
      }
      const filtered = closed.filter((r) => r.clientKey !== clientKey);
      return filtered.length > 0 ? ensureTrailingEmptyRow(filtered) : [buildNewWorkRow([], workDayModel)];
    });
  };

  const insertRowInGap = useCallback(
    (afterClientKey: string, gap: { timeFrom: string; timeTo: string }) => {
      setRows((prev) => {
        const visible = prev.filter((r) => !r.markedDelete);
        const afterIdx = visible.findIndex((r) => r.clientKey === afterClientKey);
        const following = afterIdx >= 0 ? visible[afterIdx + 1] : undefined;
        if (
          following &&
          isUnassignedGapFillRow(following) &&
          following.timeFrom.slice(0, 5) === gap.timeFrom &&
          following.timeTo.slice(0, 5) === gap.timeTo
        ) {
          return prev;
        }
        const next = insertVisibleRowAfter(
          prev,
          afterClientKey,
          buildGapFillWorkRow(gap.timeFrom, gap.timeTo),
        );
        return ensureTrailingEmptyRow(resolveDayEntryOverlaps(next));
      });
    },
    [ensureTrailingEmptyRow],
  );

  const addBreakRow = () => {
    const visible = rows.filter((r) => !r.markedDelete);
    const content = visible.filter(rowHasContent);
    const range = suggestBreakRange(content, workDayModel);
    if (!range) {
      toast.error(
        "Pause konnte nicht vorausgefüllt werden — bitte zuerst einen Zeiteintrag erfassen oder das Arbeitszeitmodell prüfen.",
      );
      return;
    }
    const breakRow: DayEntryRow = {
      clientKey: nextRowKey(),
      rowKind: "break",
      project: null,
      positionId: null,
      timeFrom: range.timeFrom,
      timeTo: range.timeTo,
      durationInput: formatDurationInputFromRange(range.timeFrom, range.timeTo),
      comment: "Pause",
      isHomeoffice: false,
    };
    const withBreak = [...content, breakRow];
    const trailing = buildNewWorkRow(withBreak, workDayModel);
    const deleted = rows.filter((r) => r.markedDelete);
    setRows([...deleted, ...withBreak, trailing]);
  };

  const toggleRowKind = useCallback(
    (clientKey: string) => {
      setRows((prev) => {
        const next = prev.map((row) => {
          if (row.clientKey !== clientKey || row.readOnly) return row;
          if (row.rowKind === "work") {
            return {
              ...row,
              rowKind: "break" as const,
              project: null,
              positionId: null,
              positionRequired: false,
              comment: row.comment.trim() ? row.comment : "Pause",
              isHomeoffice: false,
            };
          }
          const wasDefaultPauseComment = row.comment.trim().toLowerCase() === "pause";
          return {
            ...row,
            rowKind: "work" as const,
            comment: wasDefaultPauseComment ? "" : row.comment,
          };
        });
        return ensureTrailingEmptyRow(resolveDayEntryOverlaps(next));
      });
    },
    [ensureTrailingEmptyRow],
  );

  const applyPendingDurations = (source: DayEntryRow[]): DayEntryRow[] => {
    const next = source.map((row) => {
      if (row.readOnly || row.markedDelete) return row;
      if (!parseDurationInput(row.durationInput)) return row;
      return { ...row, ...syncDayEntryTimeFields(row, { durationInput: row.durationInput }) };
    });
    return resolveDayEntryOverlaps(next);
  };

  const validateRows = (source: DayEntryRow[]): string | null => {
    const visible = source.filter((r) => !r.markedDelete);
    for (const row of visible) {
      if (!rowHasContent(row)) continue;
      if (!row.timeFrom || !row.timeTo) {
        return "Bitte Von und Bis für jede Zeile vollständig ausfüllen.";
      }
      if (computeHoursFromTimeRange(row.timeFrom, row.timeTo) <= 0) {
        return "Die Endzeit muss nach der Startzeit liegen.";
      }
      if (row.rowKind === "work" && projectRequired && !row.project?.value.id) {
        return "Für Monteure ist in jeder Arbeitszeile ein Projekt verpflichtend.";
      }
      if (
        row.rowKind === "work" &&
        row.positionRequired &&
        row.positionId == null &&
        rowHasContent(row)
      ) {
        return MIXED_PROJECT_POSITION_REQUIRED;
      }
    }
    const hasAnyWork = visible.some(
      (r) => r.rowKind === "work" && r.timeFrom && r.timeTo,
    );
    if (!hasAnyWork) {
      return "Bitte mindestens eine Arbeitszeitzeile ausfüllen.";
    }
    const overlapError = findDayEntryOverlapError(visible);
    if (overlapError) return overlapError;
    return null;
  };

  const handleSave = async () => {
    const syncedRows = applyPendingDurations(rows);
    setRows(syncedRows);
    const validationError = validateRows(syncedRows);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    const syncedTotal = syncedRows
      .filter((r) => !r.markedDelete)
      .reduce((sum, row) => {
        if (row.rowKind === "break" || !row.timeFrom || !row.timeTo) return sum;
        return sum + computeHoursFromTimeRange(row.timeFrom, row.timeTo);
      }, 0);
    setSaving(true);
    try {
      const payloadRows = syncedRows
        .filter((row) => !row.readOnly && (row.markedDelete || rowHasContent(row)))
        .map((row) => ({
          id: row.id,
          auftrag_id: row.rowKind === "break" ? null : row.project?.value.id ?? null,
          time_from: row.timeFrom || undefined,
          time_to: row.timeTo || undefined,
          entry_type: row.rowKind === "break" ? "break" as const : "range" as const,
          comment: row.comment || undefined,
          geschaeftsdokument_position_id:
            row.rowKind === "break" ? null : row.positionId,
          is_homeoffice:
            homeofficeAllowed && row.rowKind !== "break" ? row.isHomeoffice : false,
          delete: row.markedDelete || false,
        }));

      const result = await saveEmployeeDayBatch({
        user_login: employee.login,
        user_display: employee.display,
        work_date: workDate,
        rows: payloadRows,
      });

      invalidateCaptureCalendarCache(employee.login);
      await loadDay();
      setCalendarRefreshKey((k) => k + 1);
      const savedMsg = `${formatHours(syncedTotal)} Arbeitszeit für ${workDate} gespeichert.`;
      if (result.compliance?.requires_justification) {
        toast.warning(
          `${savedMsg} Arbeitszeitverstoß erkannt — bitte hinterlege eine Begründung. Der Tag zählt erst danach in der Zeiterfassung.`,
        );
      } else if (
        result.compliance?.violations?.length &&
        result.compliance?.justification_regime_applies
      ) {
        toast.warning(`${savedMsg} Arbeitszeitverstoß bleibt dokumentiert.`);
      } else {
        toast.success(savedMsg);
      }
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveJustification = async () => {
    const login = employee.login.trim();
    if (!login) return;
    const validationError = validateJustificationText(justificationDraft);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    if (compliance?.justification_locked) {
      toast.error(
        "Du kannst die Begründung nicht mehr ändern, da dieser Arbeitstag bereits abgeschlossen wurde.",
      );
      return;
    }
    setSavingJustification(true);
    try {
      const result = await saveDayJustification({
        user_login: login,
        work_date: workDate,
        justification: normalizeJustification(justificationDraft),
      });
      setCompliance(result.compliance);
      setJustificationDraft(result.compliance.employee_justification ?? "");
      toast.success(result.message || "Deine Begründung wurde gespeichert.");
      onSaved();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Begründung konnte nicht gespeichert werden.",
      );
    } finally {
      setSavingJustification(false);
    }
  };

  const isOwnTimes =
    Boolean(currentLogin) &&
    employee.login.trim().toLowerCase() === currentLogin.trim().toLowerCase();
  const regimeApplies =
    compliance?.justification_regime_applies ?? justificationRegimeApplies(workDate);
  const showJustificationForm =
    regimeApplies &&
    (Boolean(compliance?.violations?.length) ||
      Boolean(compliance?.employee_justification) ||
      Boolean(compliance?.requires_justification));
  const showHistoricalGfOnly =
    Boolean(compliance?.historical_gf_review) && !regimeApplies;
  const canSaveJustification =
    isOwnTimes &&
    !compliance?.justification_locked &&
    isJustificationValid(justificationDraft) &&
    normalizeJustification(justificationDraft) !==
      normalizeJustification(compliance?.employee_justification ?? "");

  const employeePickerField = allowEmployeePicker ? (
    <EmployeeLockedAutocomplete
      value={employeeOption}
      onChange={setEmployeeOption}
    />
  ) : (
    <label className="block space-y-1">
      <span className="eh-label">Mitarbeiter</span>
      <div className="eh-input flex items-center bg-fill-tertiary/40 text-label">
        {employee.display || employee.login}
      </div>
    </label>
  );

  const monthCalendar = (
    <CaptureMonthCalendar
      userLogin={employee.login}
      selectedDate={workDate}
      onSelectDate={setWorkDate}
      refreshKey={calendarRefreshKey}
    />
  );

  return (
    <div
      className={clsx(
        "flex flex-col gap-4 lg:flex-row lg:items-start",
        isMobile && "pb-24",
      )}
    >
      <aside className="eh-ze-month-calendar-aside w-full shrink-0 lg:w-[22rem] xl:w-[26rem] 2xl:w-[28rem]">
        {monthCalendar}
      </aside>

      <div className="min-w-0 flex-1 space-y-4">
        {isMobile ? (
          <div className="space-y-3 rounded-ios border border-separator bg-card p-4 shadow-ios">
            {allowEmployeePicker && employeePickerField}
            <div className="text-center text-sm font-semibold text-label">
              {formatDayLabelDe(workDate)}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-separator bg-card text-xl text-label transition active:bg-fill-tertiary"
                aria-label="Vorheriger Tag"
                onClick={() => setWorkDate((d) => shiftDateIso(d, -1))}
              >
                ‹
              </button>
              <input
                type="date"
                className="eh-input eh-ze-time-input h-12 min-w-0 flex-1 text-center font-medium"
                value={workDate}
                onChange={(e) => e.target.value && setWorkDate(e.target.value)}
              />
              <button
                type="button"
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-separator bg-card text-xl text-label transition active:bg-fill-tertiary"
                aria-label="Nächster Tag"
                onClick={() => setWorkDate((d) => shiftDateIso(d, 1))}
              >
                ›
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={workDate === todayIso() ? "primary" : "secondary"}
                size="lg"
                onClick={() => setWorkDate(todayIso())}
              >
                Heute
              </Button>
              <Button
                variant={workDate === yesterdayIso() ? "primary" : "secondary"}
                size="lg"
                onClick={() => setWorkDate(yesterdayIso())}
              >
                Gestern
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-3 rounded-ios border border-separator bg-card p-4 shadow-ios">
            <div className="min-w-[200px] flex-1">{employeePickerField}</div>
            <label className="space-y-1">
              <span className="eh-label">Datum</span>
              <input
                type="date"
                className="eh-input"
                value={workDate}
                onChange={(e) => setWorkDate(e.target.value)}
              />
            </label>
            <div className="flex gap-2">
              <Button
                variant={workDate === todayIso() ? "primary" : "secondary"}
                size="sm"
                onClick={() => setWorkDate(todayIso())}
              >
                Heute
              </Button>
              <Button
                variant={workDate === yesterdayIso() ? "primary" : "secondary"}
                size="sm"
                onClick={() => setWorkDate(yesterdayIso())}
              >
                Gestern
              </Button>
            </div>
            <Button variant="secondary" size="sm" onClick={() => addBreakRow()} disabled={loading}>
              + Pause
            </Button>
          </div>
        )}

        <p className="text-sm text-label-secondary">
          {isMobile ? (
            <>
              <span className="font-medium text-label">Von</span> und{" "}
              <span className="font-medium text-label">Bis</span> eintragen — die Dauer wird
              automatisch berechnet.
            </>
          ) : (
            <>
              Dauer als <span className="font-medium text-label">1,5</span> (Stunden) oder{" "}
              <span className="font-medium text-label">1:30</span> eingeben – Bis wird automatisch
              berechnet.
            </>
          )}
          {workDayModel?.is_workday && workDayModel.time_from && (
            <>
              {" "}
              · Tagesstart:{" "}
              <span className="font-medium text-label">{workDayModel.time_from.slice(0, 5)}</span>
              {pauseMinutes > 0 && (
                <>
                  {" "}
                  · Pausenlänge:{" "}
                  <span className="font-medium text-label">{pauseMinutes} Min.</span>
                </>
              )}
            </>
          )}
        </p>

        {projectRequired && (
          <p className="text-sm text-label-secondary">
            <span className="font-medium text-status-orange">Monteur:</span> Projekt ist in jeder
            Arbeitszeile verpflichtend (nicht bei Pausen).
          </p>
        )}

        {showHistoricalGfOnly && compliance?.historical_gf_review && (
          <Alert variant="success" title="Historische GF-Freigabe (nur Lesen)">
            <p className="text-sm">
              {compliance.reviewed_by_display
                ? `Früher freigegeben von ${compliance.reviewed_by_display}`
                : "Früher durch die Geschäftsführung bearbeitet"}
              {compliance.reviewed_at
                ? ` am ${new Date(compliance.reviewed_at).toLocaleString("de-DE")}`
                : ""}
              .
              {compliance.review_comment ? ` Kommentar: ${compliance.review_comment}` : ""}
            </p>
          </Alert>
        )}

        {showJustificationForm && (
          <div className="space-y-3">
            {(compliance?.violations?.length ?? 0) > 0 && (
              <Alert variant="error" title="🔴 Arbeitszeitverstoß">
                <ul className="mt-1 list-disc space-y-1 pl-4 text-sm">
                  {(compliance?.violations ?? []).map((v) => (
                    <li key={v.code}>{v.message}</li>
                  ))}
                </ul>
                {compliance?.requires_justification ? (
                  <>
                    <p className="mt-2 text-sm font-medium text-status-orange">
                      🟠 Deine Begründung fehlt noch.
                    </p>
                    <p className="mt-1 text-sm text-label-secondary">
                      Dieser Tag wird erst in der Zeiterfassung berücksichtigt, wenn du eine
                      Begründung hinterlegt hast.
                    </p>
                  </>
                ) : compliance?.has_justification ? (
                  <p className="mt-2 text-sm font-medium text-status-green">
                    ✅ Du hast eine Begründung hinterlegt.
                  </p>
                ) : null}
              </Alert>
            )}

            {compliance?.historical_gf_review && (
              <Alert variant="success" title="Historische GF-Freigabe (nur Lesen)">
                <p className="text-sm">
                  {compliance.reviewed_by_display
                    ? `Früher freigegeben von ${compliance.reviewed_by_display}`
                    : "Früher durch die Geschäftsführung bearbeitet"}
                  {compliance.reviewed_at
                    ? ` am ${new Date(compliance.reviewed_at).toLocaleString("de-DE")}`
                    : ""}
                  .
                  {compliance.review_comment
                    ? ` Kommentar: ${compliance.review_comment}`
                    : ""}{" "}
                  Neue Freigaben sind nicht mehr möglich.
                </p>
              </Alert>
            )}

            {(Boolean(compliance?.requires_justification) ||
              Boolean(compliance?.has_justification)) && (
              <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
                <h3 className="text-sm font-semibold text-label">
                  Begründung zum Arbeitszeitverstoß
                </h3>
                <p className="mt-2 whitespace-pre-line text-sm text-label-secondary">
                  {JUSTIFICATION_HINT}
                </p>
                <label className="mt-3 block space-y-1">
                  <span className="eh-label">
                    Bitte begründe, warum du die vorgeschriebenen Arbeitszeit- oder
                    Pausenregelungen an diesem Tag nicht einhalten konntest.
                  </span>
                  <textarea
                    className="eh-input min-h-[7rem] w-full resize-y"
                    value={justificationDraft}
                    disabled={
                      !isOwnTimes ||
                      Boolean(compliance?.justification_locked) ||
                      savingJustification
                    }
                    onChange={(e) => setJustificationDraft(e.target.value)}
                    placeholder="Deine Begründung …"
                  />
                </label>
                <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
                  <span
                    className={clsx(
                      "text-xs",
                      isJustificationValid(justificationDraft)
                        ? "text-label-secondary"
                        : "text-status-orange",
                    )}
                  >
                    {formatJustificationCounter(justificationDraft)}
                  </span>
                  {compliance?.work_hours != null && (
                    <span className="text-xs text-label-secondary">
                      Arbeitszeit {formatHoursHm(Number(compliance.work_hours))} h
                      {compliance.break_minutes != null
                        ? ` · Pause ${compliance.break_minutes} Min.`
                        : ""}
                    </span>
                  )}
                </div>
                {!isOwnTimes ? (
                  <p className="mt-2 text-sm text-label-secondary">
                    Die Begründung kann nur der betroffene Mitarbeiter selbst hinterlegen.
                  </p>
                ) : compliance?.justification_locked ? (
                  <p className="mt-2 text-sm text-label-secondary">
                    Du kannst diese Begründung nicht mehr ändern, da der Arbeitstag bereits
                    abgeschlossen wurde.
                  </p>
                ) : (
                  <div className="mt-3">
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={!canSaveJustification || savingJustification}
                      onClick={() => void handleSaveJustification()}
                    >
                      {savingJustification ? "Speichern…" : "Begründung speichern"}
                    </Button>
                  </div>
                )}
                {compliance?.justification_created_at && (
                  <p className="mt-2 text-xs text-label-secondary">
                    Begründung eingereicht am{" "}
                    {new Date(compliance.justification_created_at).toLocaleString("de-DE")}
                    {compliance.justification_updated_at &&
                    compliance.justification_updated_at !== compliance.justification_created_at
                      ? ` · zuletzt geändert am ${new Date(
                          compliance.justification_updated_at,
                        ).toLocaleString("de-DE")}`
                      : ""}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : (
          <>
            <DayEntryMobileCards
              rows={activeRows}
              projectRequired={projectRequired}
              homeofficeAllowed={homeofficeAllowed}
              dense={!isMobile}
              onUpdateRow={updateRow}
              onRemoveRow={removeRow}
              onToggleRowKind={toggleRowKind}
              onRowBlur={handleRowBlur}
              onDurationBlur={handleDurationBlur}
              onInsertInGap={insertRowInGap}
              forUserLogin={employee.login || null}
            />
            {isMobile && (
              <Button
                variant="secondary"
                size="lg"
                className="w-full"
                onClick={() => addBreakRow()}
              >
                + Pause hinzufügen
              </Button>
            )}
            {!isMobile && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-ios border border-separator bg-fill-tertiary/40 px-4 py-3">
                <div>
                  <div className="text-xs text-label-secondary">Arbeitszeit (ohne Pausen)</div>
                  <div className="text-lg font-semibold tabular-nums text-accent">
                    {formatHours(dayTotal)}
                  </div>
                </div>
                <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                  {saving ? "Speichern…" : "Tag speichern"}
                </Button>
              </div>
            )}
          </>
        )}

        {isMobile && !loading && (
          <div className="eh-ze-save-bar sticky bottom-0 z-20 -mx-1 border-t border-separator bg-card/95 px-1 pt-3 shadow-[0_-4px_16px_rgba(15,23,42,0.08)] backdrop-blur-sm">
            <div className="flex items-center justify-between gap-3">
              <div className="shrink-0">
                <div className="text-xs text-label-secondary">Arbeitszeit</div>
                <div className="text-lg font-semibold tabular-nums text-accent">
                  {formatHours(dayTotal)}
                </div>
              </div>
              <Button
                variant="primary"
                size="lg"
                className="flex-1"
                onClick={() => void handleSave()}
                disabled={saving}
              >
                {saving ? "Speichern…" : "Tag speichern"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
